// src/theme/palette.ts
//
// The MUI palette for the INTERNAL console, derived entirely from the ramps in
// `./brand` — which are copied verbatim from the product SPA and are already a
// solved problem: the categorical ramps are stepped per mode and pass six
// colourimetric checks (luminance band, chroma floor, adjacent CVD separation
// for protan/deutan/tritan, normal-vision floor, contrast against the surface),
// re-run in CI by `brand.test.ts`. Re-deriving them here would be pure risk.
//
// ─── WHAT DIVERGES, AND WHY ─────────────────────────────────────────────────
//
// PRIMARY IS INDIGO, NOT BLUE. Two decisions pull in opposite directions and
// both are right: reuse the ramps, fork the chrome. An internal console that
// looks like the customer product is a screenshot waiting to be mistaken for
// the customer product — and these screenshots contain numbers that must never
// be quoted as cash. Indigo is already a validated ramp in the same system
// (it is the product's `secondary`), so promoting it costs nothing in rigour
// and changes the page's identity at a glance.
//
// The consequence is that every contrast figure in the product's palette header
// was measured against BLUE. Those numbers do not transfer. `brand.test.ts` and
// `typography.test.ts` are ported precisely because the accent moved: they are
// the cheap way to find out whether the swap broke a ratio.
//
// This file names no colours of its own. If a value is missing here, add the
// step to a ramp in `brand.ts` — do not inline a hex.

import type { PaletteOptions } from '@mui/material/styles'
import { blue, gold, green, indigo, neutral, red } from './brand'

/** `alpha` on a hex, for the translucent action states MUI expects. */
const a = (hex: string, alpha: number) => {
  const h = hex.replace('#', '')
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const internalLight: PaletteOptions = {
  primary: { main: indigo[600], light: indigo[400], dark: indigo[800], contrastText: neutral[0] },
  // Blue becomes the counterweight — the mirror image of the product, which is
  // the point. It marks structural chrome that should recede from primary.
  secondary: { main: blue[700], light: blue[500], dark: blue[900], contrastText: neutral[0] },
  error: { main: red[600], light: red[400], dark: red[800], contrastText: neutral[0] },
  warning: { main: gold[600], light: gold[400], dark: gold[800], contrastText: neutral[0] },
  success: { main: green[600], light: green[400], dark: green[800], contrastText: neutral[0] },
  info: { main: blue[700], light: blue[500], dark: blue[900], contrastText: neutral[0] },
  background: { default: neutral[50], paper: neutral[0] },
  text: {
    primary: neutral[900],
    secondary: neutral[600],
    // ONLY for disabled controls, where WCAG exempts contrast. An informational
    // caption is not disabled — use `secondary`. In the product a live audit
    // found this token carrying a "figures are simulated" disclaimer, which is
    // exactly the class of mistake this app cannot afford: our disclaimers are
    // the load-bearing part of the number.
    disabled: neutral[400],
  },
  divider: neutral[200],
  action: {
    hover: a(neutral[900], 0.04),
    selected: a(indigo[600], 0.09),
    focus: a(indigo[600], 0.15),
    disabled: neutral[400],
    disabledBackground: neutral[150],
  },
}

export const internalDark: PaletteOptions = {
  primary: { main: indigo[300], light: indigo[200], dark: indigo[500], contrastText: neutral[975] },
  secondary: { main: blue[400], light: blue[300], dark: blue[600], contrastText: neutral[975] },
  error: { main: red[400], light: red[300], dark: red[600], contrastText: neutral[975] },
  warning: { main: gold[400], light: gold[300], dark: gold[600], contrastText: neutral[975] },
  success: { main: green[400], light: green[300], dark: green[600], contrastText: neutral[975] },
  info: { main: blue[300], light: blue[200], dark: blue[500], contrastText: neutral[975] },
  // Canvas darker than paper so elevation reads by lightness, not by shadow —
  // shadows barely register on a dark ground.
  background: { default: neutral[975], paper: neutral[900] },
  text: { primary: neutral[100], secondary: neutral[400], disabled: neutral[600] },
  divider: neutral[800],
  action: {
    hover: a(neutral[0], 0.06),
    selected: a(indigo[300], 0.18),
    focus: a(indigo[300], 0.26),
    disabled: neutral[600],
    disabledBackground: neutral[850],
  },
}
